import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Mail, 
  Search, 
  Eye, 
  Trash2, 
  Archive,
  CheckCircle,
  Clock,
  X,
  User,
  Calendar,
  Tag,
  MessageSquare,
  Loader2
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useContacts, STATUTS_MESSAGE, SUJETS_CONTACT } from '../../hooks/useContacts';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: var(--text-dark);
  font-weight: 700;
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  max-width: 400px;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-light);
  }

  input {
    width: 100%;
    padding: 0.875rem 1rem 0.875rem 2.75rem;
    border: 2px solid var(--border-light);
    border-radius: var(--radius-md);
    font-size: 0.95rem;

    &:focus {
      outline: none;
      border-color: var(--primary-blue);
    }
  }
`;

const FilterSelect = styled.select`
  padding: 0.875rem 2.5rem 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 0.95rem;
  background: var(--white);
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  display: flex;
  align-items: center;
  gap: 1rem;

  .icon {
    width: 48px;
    height: 48px;
    background: ${props => props.$bg || 'linear-gradient(135deg, var(--primary-blue), #004d8f)'};
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--white);
  }

  .content {
    .value {
      font-size: 1.5rem;
      font-weight: 800;
      color: var(--text-dark);
      font-family: var(--font-secondary);
    }

    .label {
      font-size: 0.85rem;
      color: var(--text-light);
    }
  }
`;

const Table = styled.table`
  width: 100%;
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 1.25rem;
  background: var(--off-white);
  font-weight: 600;
  color: var(--text-dark);
  font-size: 0.9rem;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-light);
  font-size: 0.95rem;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.875rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: 0.85rem;
  background: ${props => props.$bg};
  color: ${props => props.$color};
  white-space: nowrap;
`;

const ActionButton = styled.button`
  padding: 0.5rem;
  border-radius: var(--radius-md);
  color: var(--text-medium);
  cursor: pointer;
  transition: var(--transition);
  margin-right: 0.5rem;

  &:hover {
    background: var(--background-light);
    color: var(--primary-blue);
  }

  &.archive:hover {
    background: #FEF3C7;
    color: #D97706;
  }

  &.delete:hover {
    background: #FEE2E2;
    color: #DC2626;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-xl);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 2px solid var(--border-light);
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  button {
    color: var(--white);
    opacity: 0.8;

    &:hover {
      opacity: 1;
    }
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  svg {
    color: var(--primary-blue);
    flex-shrink: 0;
    margin-top: 0.2rem;
  }

  .label {
    font-size: 0.85rem;
    color: var(--text-light);
    margin-bottom: 0.25rem;
  }

  .value {
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const MessageBox = styled.div`
  background: var(--background-light);
  border-radius: var(--radius-md);
  padding: 1.5rem;
  line-height: 1.7;
  color: var(--text-medium);
  white-space: pre-wrap;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &.primary {
    background: var(--primary-blue);
    color: var(--white);

    &:hover {
      background: #004d8f;
    }
  }

  &.secondary {
    background: var(--background-light);
    color: var(--text-dark);

    &:hover {
      background: var(--border-light);
    }
  }

  &.archive {
    background: #FEF3C7;
    color: #D97706;

    &:hover {
      background: #FDE68A;
    }
  }
`;

const AdminContacts = () => {
  const { messages, loading, stats, markAsRead, archiveMessage, deleteMessage } = useContacts();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);

  // Filtrer les messages
  const filteredMessages = messages.filter(m => {
    const matchesSearch = 
      m.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || m.statut === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleOpenMessage = (message) => {
    setSelectedMessage(message);
    // Marquer comme lu si non lu
    if (message.statut === 'non_lu') {
      markAsRead(message.id);
    }
  };

  const handleArchive = async () => {
    if (!selectedMessage) return;
    await archiveMessage(selectedMessage.id);
    setSelectedMessage(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ')) {
      await deleteMessage(id);
      if (selectedMessage.id === id) {
        setSelectedMessage(null);
      }
    }
  };

  const getSujetLabel = (value) => {
    return SUJETS_CONTACT.find(s => s.value === value).label || value;
  };

  return (
    <AdminLayout title="Messages de Contact">
      <HeaderActions>
        <Title>Messages reçus</Title>
        <SearchBox>
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher par nom, email ou contenu..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Tous les statuts</option>
          <option value="non_lu">Non lus</option>
          <option value="lu">Lus</option>
          <option value="archive">Archivés</option>
        </FilterSelect>
      </HeaderActions>

      <StatsGrid>
        <StatCard>
          <div className="icon">
            <Mail size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.total}</div>
            <div className="label">Total messages</div>
          </div>
        </StatCard>
        <StatCard $bg="linear-gradient(135deg, #DC2626, #EF4444)">
          <div className="icon">
            <Clock size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.nonLus}</div>
            <div className="label">Non lus</div>
          </div>
        </StatCard>
        <StatCard $bg="linear-gradient(135deg, #3B82F6, #60A5FA)">
          <div className="icon">
            <CheckCircle size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.lus}</div>
            <div className="label">Lus</div>
          </div>
        </StatCard>
        <StatCard $bg="linear-gradient(135deg, #6B7280, #9CA3AF)">
          <div className="icon">
            <Archive size={24} />
          </div>
          <div className="content">
            <div className="value">{stats.archives}</div>
            <div className="label">Archivés</div>
          </div>
        </StatCard>
      </StatsGrid>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <Loader2 size={48} className="animate-spin" />
          <p>Chargement...</p>
        </div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Expéditeur</Th>
              <Th>Sujet</Th>
              <Th>Date</Th>
              <Th>Statut</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredMessages.length === 0 ? (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center', padding: '2rem' }}>
                  Aucun message trouvé
                </Td>
              </tr>
            ) : (
              filteredMessages.map((message) => (
                <tr key={message.id} style={{ fontWeight: message.statut === 'non_lu' ? '600' : 'normal' }}>
                  <Td>
                    <div>{message.nom}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-light)' }}>{message.email}</div>
                  </Td>
                  <Td>{getSujetLabel(message.sujet)}</Td>
                  <Td>
                    {message.date.toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Td>
                  <Td>
                    <StatusBadge 
                      $bg={STATUTS_MESSAGE[message.statut].bg} 
                      $color={STATUTS_MESSAGE[message.statut].color}
                    >
                      {STATUTS_MESSAGE[message.statut].label}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <ActionButton onClick={() => handleOpenMessage(message)} title="Voir">
                      <Eye size={18} />
                    </ActionButton>
                    <ActionButton 
                      className="archive" 
                      onClick={() => archiveMessage(message.id)}
                      title="Archiver"
                      disabled={message.statut === 'archive'}
                    >
                      <Archive size={18} />
                    </ActionButton>
                    <ActionButton 
                      className="delete" 
                      onClick={() => handleDelete(message.id)}
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </ActionButton>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* Modal de lecture */}
      {selectedMessage && (
        <ModalOverlay onClick={() => setSelectedMessage(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>
                <Mail size={24} />
                Message de {selectedMessage.nom}
              </h2>
              <button onClick={() => setSelectedMessage(null)}>
                <X size={24} />
              </button>
            </ModalHeader>

            <ModalBody>
              <InfoGrid>
                <InfoItem>
                  <User size={20} />
                  <div>
                    <div className="label">Nom</div>
                    <div className="value">{selectedMessage.nom}</div>
                  </div>
                </InfoItem>

                <InfoItem>
                  <Mail size={20} />
                  <div>
                    <div className="label">Email</div>
                    <div className="value">{selectedMessage.email}</div>
                  </div>
                </InfoItem>

                <InfoItem>
                  <Tag size={20} />
                  <div>
                    <div className="label">Sujet</div>
                    <div className="value">{getSujetLabel(selectedMessage.sujet)}</div>
                  </div>
                </InfoItem>

                <InfoItem>
                  <Calendar size={20} />
                  <div>
                    <div className="label">Date</div>
                    <div className="value">
                      {selectedMessage.date.toLocaleString('fr-FR')}
                    </div>
                  </div>
                </InfoItem>

                <InfoItem>
                  <CheckCircle size={20} />
                  <div>
                    <div className="label">Statut</div>
                    <div className="value">
                      <StatusBadge 
                        $bg={STATUTS_MESSAGE[selectedMessage.statut].bg} 
                        $color={STATUTS_MESSAGE[selectedMessage.statut].color}
                      >
                        {STATUTS_MESSAGE[selectedMessage.statut].label}
                      </StatusBadge>
                    </div>
                  </div>
                </InfoItem>
              </InfoGrid>

              <div style={{ marginBottom: '1rem' }}>
                <div className="label" style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Message</div>
                <MessageBox>{selectedMessage.message}</MessageBox>
              </div>

              <ButtonGroup>
                <Button className="secondary" onClick={() => setSelectedMessage(null)}>
                  Fermer
                </Button>
                {selectedMessage.statut !== 'archive' && (
                  <Button className="archive" onClick={handleArchive}>
                    <Archive size={18} />
                    Archiver
                  </Button>
                )}
                <Button className="primary" onClick={() => window.open(`mailto:${selectedMessage.email}`)}>
                  <Mail size={18} />
                  Répondre
                </Button>
              </ButtonGroup>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </AdminLayout>
  );
};

export default AdminContacts;
